/**
 * InfoBanner.tsx - Banner informativo colapsable
 * 
 * UID-shell-02 | Shell | HTML Canónico: P2 (Valida v9)
 * 
 * Banner con:
 * - Icono de alerta/info
 * - Mensaje descriptivo
 * - Botón de cerrar
 * - Variantes: valida (warning), info, success, error
 * 
 * @migrated 2026-01-12 - Tokens centralizados del Design System
 * @migrated 2026-02-26 - DS v2 (F8.07)
 */

import { useState, type CSSProperties, type FC } from 'react';

// Import tokens DS v2
import {
  colors,
  typography,
  borderRadius,
  spacing,
  transitions,
} from '@/design-system/v2';

// ============================================================================
// TYPES
// ============================================================================

export type BannerVariant = 'normaliza' | 'valida' | 'info' | 'success' | 'error';

export interface InfoBannerProps {
  /** Variante del banner */
  variant?: BannerVariant;
  /** Contenido del mensaje (puede incluir JSX) */
  children: React.ReactNode;
  /** Si el banner es visible */
  visible?: boolean;
  /** Si se puede cerrar */
  dismissible?: boolean;
  /** Callback al cerrar */
  onClose?: () => void;
  /** Clase CSS adicional */
  className?: string;
}

// ============================================================================
// VARIANT COLORS
// ============================================================================

const variantColors: Record<BannerVariant, { border: string; bg: string; icon: string }> = {
  normaliza: {
    border: 'rgba(45, 212, 191, 0.3)',
    bg: 'rgba(45, 212, 191, 0.08)',
    icon: colors.primary,
  },
  valida: {
    border: 'rgba(251, 191, 36, 0.3)',
    bg: 'rgba(251, 191, 36, 0.08)',
    icon: colors.warning,
  },
  info: {
    border: 'rgba(96, 165, 250, 0.3)',
    bg: 'rgba(96, 165, 250, 0.08)',
    icon: '#60A5FA',
  },
  success: {
    border: 'rgba(163, 230, 53, 0.3)',
    bg: 'rgba(163, 230, 53, 0.08)',
    icon: colors.scores.high,
  },
  error: {
    border: 'rgba(248, 113, 113, 0.3)',
    bg: 'rgba(248, 113, 113, 0.08)',
    icon: colors.error,
  },
};

// ============================================================================
// STYLES (extraídos de P2_Valida_v9_fixed.html)
// ============================================================================

const getBannerStyle = (variant: BannerVariant): CSSProperties => ({
  margin: '0 24px',
  marginTop: '12px',
  padding: '12px 20px',
  borderRadius: borderRadius.lg,
  display: 'flex',
  alignItems: 'center',
  gap: spacing[3],
  fontSize: typography.fontSize.base,
  background: variantColors[variant].bg,
  border: `1px solid ${variantColors[variant].border}`,
});

const iconStyle = (variant: BannerVariant): CSSProperties => ({
  width: '20px',
  height: '20px',
  strokeWidth: 2,
  fill: 'none',
  flexShrink: 0,
  stroke: variantColors[variant].icon,
});

const textStyle: CSSProperties = {
  flex: 1,
  color: colors.fg.secondary,
};

const textStrongStyle: CSSProperties = {
  color: colors.fg.primary,
};

const closeButtonBaseStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: colors.fg.muted,
  cursor: 'pointer',
  padding: '4px',
  borderRadius: borderRadius.sm,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitions.base,
};

// ============================================================================
// ICONS (SVG inline del HTML canónico)
// ============================================================================

const WarningIcon: FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" style={style}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon: FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" style={style}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const SuccessIcon: FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" style={style}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ErrorIcon: FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" style={style}>
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

/** Icono bombilla para variante normaliza (del HTML P1) */
const LightbulbIcon: FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 24 24" style={style}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

const CloseIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    width={16}
    height={16}
    stroke="currentColor"
    strokeWidth={2}
    fill="none"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// ============================================================================
// ICON SELECTOR
// ============================================================================

const getVariantIcon = (variant: BannerVariant, style: CSSProperties) => {
  switch (variant) {
    case 'normaliza':
      return <LightbulbIcon style={style} />;
    case 'valida':
      return <WarningIcon style={style} />;
    case 'info':
      return <InfoIcon style={style} />;
    case 'success':
      return <SuccessIcon style={style} />;
    case 'error':
      return <ErrorIcon style={style} />;
    default:
      return <InfoIcon style={style} />;
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export const InfoBanner: FC<InfoBannerProps> = ({
  variant = 'valida',
  children,
  visible = true,
  dismissible = true,
  onClose,
  className,
}) => {
  const [closeHovered, setCloseHovered] = useState(false);

  // No renderizar si no es visible
  if (!visible) {
    return null;
  }

  const handleClose = () => {
    onClose?.();
  };

  // Computed close button style
  const closeButtonStyle: CSSProperties = {
    ...closeButtonBaseStyle,
    color: closeHovered ? colors.fg.primary : colors.fg.muted,
    background: closeHovered ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
  };

  return (
    <div style={getBannerStyle(variant)} className={className} role="alert">
      {/* Icon */}
      {getVariantIcon(variant, iconStyle(variant))}

      {/* Text content */}
      <span style={textStyle}>{children}</span>

      {/* Close button */}
      {dismissible && (
        <button
          type="button"
          style={closeButtonStyle}
          onClick={handleClose}
          onMouseEnter={() => setCloseHovered(true)}
          onMouseLeave={() => setCloseHovered(false)}
          aria-label="Cerrar banner"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// HELPER COMPONENT: Strong text
// ============================================================================

export const BannerStrong: FC<{ children: React.ReactNode }> = ({ children }) => (
  <strong style={textStrongStyle}>{children}</strong>
);

export default InfoBanner;
